import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useGetCartGroupsQuery } from "@monorepo/services/api/carts";
import theme from "@monorepo/styles/theme";
import { InputBase, InputLabel, MenuItem, SelectChangeEvent, styled } from "@mui/material";
import { Select } from "@mui/material";
import Box from "@mui/material/Box";

interface IServiceCartSelectProps {
  defaultValue?: string;
  disabled?: boolean;
  fieldName: string;
}

const StyledInput = styled(InputBase)(() => ({
  fontSize: theme.typography.body2.fontSize,
  backgroundColor: "transparent",
  boxSizing: "border-box",
  borderRadius: 0,
  outline: 0,
  border: 0,
  borderBottom: `1px solid ${theme.palette.geyserGray.main}`,
  width: "100%",
  "&:selection": {
    background: theme.palette.primary.main,
    color: theme.palette.text.secondary,
  },
  "&::placeholder": {
    color: theme.palette.nobelGray.main,
  },
  "&::read-only": {
    pointerEvents: "none",
    background: theme.palette.background.default,
    color: theme.palette.text.disabled,
  },
  "&:focus, :focus-visible, :hover": {
    outline: 0,
    borderBottom: `1px solid ${theme.palette.primary.main}`,
    "& .MuiSvgIcon-root": {
      color: theme.palette.primary.main,
      stroke: theme.palette.primary.main,
    },
  },
  "&:disabled": {
    background: theme.palette.nobelGray.main,
  },
}));

export const ServiceCartSelect: React.FC<IServiceCartSelectProps> = ({ defaultValue, disabled, fieldName }) => {
  const { register } = useFormContext();
  const { data: cartGroups } = useGetCartGroupsQuery();
  const [viewValue, setViewValue] = useState<string | undefined>(defaultValue);
  useEffect(() => {
    setViewValue(defaultValue);
  }, [defaultValue]);

  const onChange = (e: SelectChangeEvent) => {
    setViewValue(e.target.value);
  };
  const { t } = useTranslation();

  const options = cartGroups?.map(({ name, id: cartGroupId }) => ({
    value: cartGroupId,
    label: name,
  }));

  if (options && options.length) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyItems: "space-between",
          "& label": { padding: "6px 6px 3px 3px" },
        }}
      >
        <InputLabel
          sx={{
            fontSize: theme.typography.inputLabel.fontSize,
            color: theme.palette.text.secondary,
          }}
          htmlFor={fieldName}
        >
          {t("cart.carts")}
        </InputLabel>

        <Select
          sx={{
            maxWidth: {
              sm: "340px",
            },
          }}
          disabled={disabled || false}
          inputProps={{
            sx: {
              padding: "6px 6px 3px 6px",
              "&:focus": {
                borderTopRightRadius: theme.shape.borderRadius,
                borderTopLeftRadius: theme.shape.borderRadius,
              },
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                padding: 1,
                "& .MuiButtonBase-root.MuiMenuItem-root:hover": {
                  bgcolor: theme.palette.card.dark,
                  color: theme.palette.primary.contrastText,
                  borderRadius: "5px",
                },
              },
            },
          }}
          input={<StyledInput />}
          variant="standard"
          id={fieldName}
          {...register(fieldName)}
          onChange={onChange}
          value={viewValue || options[0].value}
        >
          {options.map(({ value, label }) => (
            <MenuItem
              sx={{
                minWidth: "0px",
              }}
              key={value}
              value={value}
            >
              <Box
                sx={{
                  height: "100%",
                  verticalAlign: "middle",
                  display: "inline-block",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  width: "100%",
                  maxWidth: {
                    xs: "290px",
                    sm: "400px",
                    md: "485px",
                    lg: "600px",
                  },
                }}
              >
                {label}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  }

  return null;
};
